import { Routes, Route, Navigate } from 'react-router-dom'
import React, { Suspense, lazy } from 'react'
import { Toaster } from 'sonner'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { Layout } from './components/layout/Layout'
import { LoginPage } from './pages/auth/LoginPage'

// Lazy-loaded pages to reduce initial bundle size
const Orders = lazy(() => import('./pages/Orders'))
const OrderDetailPage = lazy(() => import('./pages/orders/OrderDetailPage'))
const ProductsPage = lazy(() => import('./pages/products/ProductsPage').then(m => ({ default: m.ProductsPage })))
const CategoriesPage = lazy(() => import('./pages/categories/CategoriesPage'))
const BrandsPage = lazy(() => import('./pages/brands/BrandsPage'))
const CollectionsPage = lazy(() => import('./pages/collections/CollectionsPage'))
const CustomersPage = lazy(() => import('./pages/customers/CustomersPage'))
const CustomerBalanceDetailPage = lazy(() => import('./pages/customers/CustomerBalanceDetailPage'))
const UsersPage = lazy(() => import('./pages/users/UsersPage'))
const WarehousesPage = lazy(() => import('./pages/warehouses/WarehousesPage'))
const InventoryPage = lazy(() => import('./pages/inventory/InventoryPage'))
const StockMovementsPage = lazy(() => import('./pages/inventory/StockMovementsPage'))
const StockCountListPage = lazy(() => import('./pages/StockCountListPage'))
const StockCountWizard = lazy(() => import('./components/inventory/stock-count/StockCountWizard').then(m => ({ default: m.StockCountWizard })))
const WalletDashboard = lazy(() => import('./pages/wallets/WalletDashboard'))
const WalletDetailPage = lazy(() => import('./pages/wallets/WalletDetailPage'))
const CasaWalletDetailPage = lazy(() => import('./pages/wallets/CasaWalletDetailPage'))
const CasaUsdDetailPage = lazy(() => import('./pages/wallets/CasaUsdDetailPage'))
const CasaEurDetailPage = lazy(() => import('./pages/wallets/CasaEurDetailPage'))
const CasaUahDetailPage = lazy(() => import('./pages/wallets/CasaUahDetailPage'))
const CompanyTreasuryPage = lazy(() => import('./pages/wallets/CompanyTreasuryPage'))
const ExpensesPage = lazy(() => import('./pages/expenses/ExpensesPage'))
const IncomingConstructionPage = lazy(() => import('./pages/suppliers/IncomingConstructionPage'))
const SuppliersConstructionPage = lazy(() => import('./pages/suppliers/SuppliersConstructionPage'))

// Route configuration
const protectedRoutes = [
  { path: '/orders', component: Orders },
  { path: '/orders/:id', component: OrderDetailPage },
  { path: '/products', component: ProductsPage },
  { path: '/categories', component: CategoriesPage },
  { path: '/brands', component: BrandsPage },
  { path: '/collections', component: CollectionsPage },
  { path: '/customers', component: CustomersPage },
  { path: '/customers/:customerId/balance', component: CustomerBalanceDetailPage },
  { path: '/warehouses', component: WarehousesPage },
  { path: '/inventory', component: InventoryPage },
  { path: '/inventory/:productId/history', component: StockMovementsPage },
  { path: '/inventory/stock-count', component: StockCountListPage },
  { path: '/inventory/stock-count/create', component: StockCountWizard },
  { path: '/inventory/stock-count/:id', component: StockCountWizard },
  { path: '/wallets', component: WalletDashboard },
  { path: '/wallets/casa', component: CasaWalletDetailPage },
  { path: '/wallets/casa/usd', component: CasaUsdDetailPage },
  { path: '/wallets/casa/eur', component: CasaEurDetailPage },
  { path: '/wallets/casa/uah', component: CasaUahDetailPage },
  { path: '/wallets/treasury', component: CompanyTreasuryPage },
  { path: '/wallets/:id', component: WalletDetailPage },
  { path: '/expenses', component: ExpensesPage },
  { path: '/incoming-orders', component: IncomingConstructionPage },
  { path: '/incoming-orders/:orderId/receive', component: IncomingConstructionPage },
  { path: '/incoming-shipments', component: IncomingConstructionPage },
  { path: '/incoming-shipments/:id', component: IncomingConstructionPage },
  { path: '/incoming-shipments/:orderId/receive', component: IncomingConstructionPage },
  { path: '/suppliers', component: SuppliersConstructionPage },
  { path: '/users', component: UsersPage },
]

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <AuthProvider>
          <div className="min-h-screen bg-background">
            <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/" element={<Navigate to="/orders" replace />} />
                {protectedRoutes.map(({ path, component: Component }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Component />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                ))}
              </Routes>
            </Suspense>
            <Toaster position="top-right" richColors />
          </div>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
